import { Box, Grid2 as Grid } from '@mui/material';
import { Panel } from 'reactflow';
import { FlexCenVer } from '@/UI/styles';
import { color, EL_COLORS } from '@/colors';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { CreditsInfo } from '../FlowComponents/CreditsInfo/CreditsInfo';
import { PANEL_WIDTH } from '../consts/ui';
import { NodeProperties } from './NodeProperties';
import { RunModelsSection } from './RunModelsSection';
import type { Node } from '@/types/node';

const NODES_WITH_PROPERTIES_DISPLAYED = ['wildcardV2', 'custommodelV2', 'masks', 'number_selector'];

function shouldShowNodeProperties(n: Node): boolean {
  if (!n.type) return false;
  return (
    NODES_WITH_PROPERTIES_DISPLAYED.includes(n.type) ||
    (n.type === 'muxv2' && !(n.data as { isIterator?: boolean }).isIterator) ||
    (n.type === 'multilora' && (n.data as { viewMode?: NodeViewMode }).viewMode !== NodeViewMode.Single)
  );
}

function shouldShowRunModelsSection(nodes: Node[]): boolean {
  return nodes.some((node) => node.isModel && node.type !== 'masks');
}

interface PropertiesDrawerProps {
  selectedNodes: Node[];
}

function PropertiesDrawer({ selectedNodes }: PropertiesDrawerProps) {
  const nodesWithProperties = selectedNodes.filter(shouldShowNodeProperties);

  return (
    <Panel
      position="top-right"
      style={{
        height: '100%',
        width: `${PANEL_WIDTH}px`,
        top: '0px',
        right: '0px',
        margin: 0,
      }}
      className={nodesWithProperties.length > 0 ? 'slide-right-enter' : 'slide-right-exit'}
    >
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          backgroundColor: color.Black92,
          borderLeft: `1px solid ${EL_COLORS.BoxBorder}`,
        }}
      >
        {nodesWithProperties.length > 0 && (
          <Grid container direction="column" sx={{ height: '100%' }}>
            <Grid size="auto">
              <FlexCenVer
                data-testid="properties-title"
                sx={{
                  borderBottom: `1px solid ${EL_COLORS.BoxBorder}`,
                  pl: 1.5,
                  pr: 2,
                  height: '84px',
                }}
              >
                <CreditsInfo isPaperBg={false} />
              </FlexCenVer>
            </Grid>
            <Grid size="grow" sx={{ overflowY: 'auto' }} className="wea-no-scrollbar">
              <Box sx={{ p: nodesWithProperties.length > 1 ? 0 : 2 }}>
                {nodesWithProperties.map((node) => {
                  return <NodeProperties key={node.id} node={node} selectedNodes={nodesWithProperties} />;
                })}
              </Box>
            </Grid>

            {shouldShowRunModelsSection(selectedNodes) ? (
              <Grid
                size="auto"
                sx={{
                  width: '100%',
                  borderTop: `1px solid ${color.White40_T}`,
                }}
              >
                <RunModelsSection selectedNodes={selectedNodes} />
              </Grid>
            ) : null}
          </Grid>
        )}
      </Box>
    </Panel>
  );
}

export default PropertiesDrawer;
